import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Check, ShieldCheck, Sparkles, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { isAuthenticated } from '../../Backend/services/authService';
import AuthButton from './AuthButton';
import GoogleButton from '../GoogleButton/GoogleButton';
import GooglePasswordSetup from './GooglePasswordSetup';
import styles from './RegistrationWelcome.module.css';

const SEEN_KEY = 'pm_registration_welcome_seen';

/**
 * Primeira entrada para quem ainda não tem sessão.
 *
 * O convite fica apenas na home: links públicos de clubes, convites e
 * resultados continuam acessíveis sem interromper o fluxo de quem os abriu.
 * Depois de fechado, não volta a tapar a app neste dispositivo.
 */
const RegistrationWelcome: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.userProfile.userProfile);
  const [visible, setVisible] = useState(false);
  const [showGooglePasswordSetup, setShowGooglePasswordSetup] = useState(false);
  const [googleAccountEmail, setGoogleAccountEmail] = useState('');

  useEffect(() => {
    if (pathname !== '/' || user || isAuthenticated()) {
      setVisible(false);
      return;
    }

    try {
      if (localStorage.getItem(SEEN_KEY) === '1') return;
    } catch {
      // Se o storage estiver indisponível, continua a mostrar a experiência.
    }

    const timer = window.setTimeout(() => setVisible(true), 700);
    return () => window.clearTimeout(timer);
  }, [pathname, user]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible]);

  const close = () => {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* sem storage: o estado da sessão chega para não repetir agora */
    }
    setVisible(false);
  };

  const register = () => {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* segue para o registo */
    }
    navigate('/register');
  };

  if (!visible) {
    return showGooglePasswordSetup ? (
      <GooglePasswordSetup
        email={googleAccountEmail}
        onDone={() => { setShowGooglePasswordSetup(false); navigate('/'); }}
        onSkip={() => { setShowGooglePasswordSetup(false); navigate('/'); }}
      />
    ) : null;
  }

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <section
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="registration-welcome-title"
        aria-describedby="registration-welcome-description"
      >
        <button type="button" className={styles.close} onClick={close} aria-label="Fechar">
          <X size={19} />
        </button>

        <div className={styles.icon} aria-hidden="true">
          <Sparkles size={23} />
        </div>
        <p className={styles.eyebrow}>A tua próxima partida começa aqui</p>
        <h2 id="registration-welcome-title">Cria a tua conta PadelMaster</h2>
        <p id="registration-welcome-description" className={styles.intro}>
          Guarda os teus jogos, entra em torneios e acompanha a tua evolução num só lugar.
        </p>

        <div className={styles.benefits}>
          <span><Check size={16} /> Registo gratuito</span>
          <span><Check size={16} /> Jogos e convites sempre contigo</span>
          <span><Check size={16} /> Ranking e clubes personalizados</span>
        </div>

        <div className={styles.actions}>
          <AuthButton type="button" fullWidth onClick={register} leftIcon={<ArrowRight size={18} />}>
            Criar conta grátis
          </AuthButton>
          <div className={styles.divider}><span>ou continua com</span></div>
          <div className={styles.googleButton}>
            <GoogleButton
              text="Google"
              onGoogleOnly={(response) => {
                setGoogleAccountEmail(response.user.email);
                close();
                setShowGooglePasswordSetup(true);
              }}
            />
          </div>
        </div>

        <p className={styles.loginLine}>
          Já tens conta?{' '}
          <button type="button" onClick={() => { close(); navigate('/login'); }}>
            Entrar
          </button>
        </p>
        <p className={styles.trust}><ShieldCheck size={15} /> Seguro, simples e sem compromisso.</p>
      </section>
    </div>,
    document.body,
  );
};

export default RegistrationWelcome;
